import { useCallback } from 'react';
import { Flow, SubFlow } from '../types';

/** Stub flow returned when this hook is not active */
const INACTIVE_STUB: {
    flow: Flow;
    subFlows: SubFlow[];
    getNextStep: (currentRoute: string) => Promise<string | null>;
    loading: boolean;
} = {
    flow: {
        id: 'default',
        name: 'Default Apply Flow',
        description: 'Traditional full-featured application experience',
        steps: [],
    },
    subFlows: [],
    getNextStep: () => Promise.resolve(null),
    loading: false,
};

/**
 * Hook to get the default application flow configuration
 * @param isActive - Whether this flow is currently active. If false, returns a minimal stub.
 * @returns Object containing flow definition
 */
export const useDefaultFlow = (
    isActive: boolean = true,
): {
    flow: Flow;
    subFlows: SubFlow[];
    getNextStep: (currentRoute: string) => Promise<string | null>;
    loading: boolean;
} => {
    // Default Apply Flow Definition
    const flow: Flow = isActive
        ? {
              id: 'default',
              name: 'Default Apply Flow',
              description: 'Traditional full-featured application experience',
              steps: [
                  {
                      routes: ['/default-apply/contact-info'],
                      label: 'Contact Info',
                      skipOnBack: false,
                  },
                  {
                      routes: ['/default-apply/profile-picture'],
                      label: 'Profile Picture',
                      skipOnBack: false,
                  },
                  {
                      routes: ['/default-apply/resume'],
                      label: 'Resume',
                      skipOnBack: false,
                  },
                  {
                      routes: ['/default-apply/work-experience'],
                      label: 'Work Experience',
                      skipOnBack: false,
                  },
                  {
                      routes: ['/default-apply/questions'],
                      label: 'Questions',
                      skipOnBack: false,
                  },
                  {
                      routes: ['/default-apply/documents'],
                      label: 'Documents',
                      skipOnBack: false,
                  },
                  {
                      routes: ['/default-apply/review'],
                      label: 'Review',
                      skipOnBack: false,
                  },
                  {
                      routes: ['/post-apply'],
                      label: 'Complete',
                      skipOnBack: true, // Users don't navigate back to success page
                  },
              ],
          }
        : INACTIVE_STUB.flow;

    // Static getNextStep implementation for Default Apply flow
    const getNextStep = useCallback(
        // eslint-disable-next-line @typescript-eslint/require-await
        async (currentRoute: string): Promise<string | null> => {
            // Find current step index
            const currentIndex = flow.steps.findIndex((step) => step.routes.includes(currentRoute));

            // If we're not in the flow or at the last step, return null
            if (currentIndex === -1 || currentIndex >= flow.steps.length - 1) {
                return null;
            }

            // Return next step's primary route
            return flow.steps[currentIndex + 1].routes[0];
        },
        [flow.steps],
    );

    if (!isActive) {
        return INACTIVE_STUB;
    }

    return {
        flow,
        subFlows: [], // Default Apply flow has no sub-flow detours
        getNextStep,
        loading: false, // Default Apply flow doesn't have dynamic loading
    };
};
